import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, BookOpen, Heart, Send, Play } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function fmtDate(d) {
    return new Date(d).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function extractEmbed(url) {
    if (!url) return null;
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    if (yt) return { type: "youtube", src: `https://www.youtube.com/embed/${yt[1]}` };
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (vim) return { type: "vimeo", src: `https://player.vimeo.com/video/${vim[1]}` };
    if (/\.(mp4|webm|ogg)$/i.test(url)) return { type: "file", src: url };
    return { type: "link", src: url };
}

function PostCard({ post, onLike, onCommentAdded }) {
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const embed = extractEmbed(post.video_url);

    const loadComments = async () => {
        try {
            const { data } = await http.get(`/community/posts/${post.id}/comments`);
            setComments(data);
        } catch { /* ignore */ }
    };
    const toggleComments = () => {
        const next = !showComments;
        setShowComments(next);
        if (next && comments.length === 0) loadComments();
    };
    const submitComment = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            const { data } = await http.post(`/community/posts/${post.id}/comments`, { content: text });
            setComments((c) => [...c, data]);
            setText("");
            onCommentAdded?.(post.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <article className="rounded-3xl border border-border/60 bg-card overflow-hidden" data-testid={`post-${post.id}`}>
            {post.image_url && (
                <div className="aspect-[16/9] overflow-hidden bg-muted">
                    <img src={post.image_url} alt={post.image_alt || post.title || "Post"} title={post.title || "Post"} className="w-full h-full object-cover" loading="lazy" />
                </div>
            )}
            {embed && (embed.type === "youtube" || embed.type === "vimeo") && (
                <div className="aspect-video bg-black">
                    <iframe src={embed.src} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" title="video" />
                </div>
            )}
            {embed && embed.type === "file" && (
                <video controls src={embed.src} className="w-full max-h-[500px] bg-black" />
            )}
            <div className="p-6 md:p-7">
                <div className="flex justify-between items-baseline mb-3">
                    <span className="text-xs uppercase tracking-widest text-primary">{post.user_name}</span>
                    <span className="text-xs text-muted-foreground">{fmtDate(post.created_at)}</span>
                </div>
                {post.title && <h3 className="font-serif text-2xl mb-3">{post.title}</h3>}
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/85">{post.content}</p>
                {embed && embed.type === "link" && (
                    <a href={embed.src} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs text-primary underline">
                        <Play className="w-3 h-3" /> Voir la vidéo
                    </a>
                )}
                <div className="flex items-center gap-2 mt-5 pt-4 border-t border-border/40">
                    <button
                        onClick={() => onLike(post.id)}
                        className={`inline-flex items-center gap-2 rounded-full h-9 px-4 text-sm transition-all ${
                            post.liked_by_me ? "bg-primary/15 text-primary" : "hover:bg-muted text-muted-foreground"
                        }`}
                        data-testid={`like-btn-${post.id}`}
                    >
                        <Heart className={`w-4 h-4 ${post.liked_by_me ? "fill-current" : ""}`} />
                        {post.likes_count}
                    </button>
                    <button
                        onClick={toggleComments}
                        className="inline-flex items-center gap-2 rounded-full h-9 px-4 text-sm hover:bg-muted text-muted-foreground"
                        data-testid={`comments-btn-${post.id}`}
                    >
                        <MessageCircle className="w-4 h-4" />
                        {post.comments_count}
                    </button>
                </div>
                {showComments && (
                    <div className="mt-5 space-y-4" data-testid={`comments-panel-${post.id}`}>
                        {comments.length === 0 && (
                            <p className="text-xs text-muted-foreground">Aucun commentaire — ouvrez le dialogue.</p>
                        )}
                        {comments.map((c) => (
                            <div key={c.id} className="rounded-2xl bg-muted/40 p-4">
                                <div className="flex justify-between items-baseline mb-1">
                                    <span className="text-xs font-medium">{c.user_name}</span>
                                    <span className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</span>
                                </div>
                                <p className="text-sm whitespace-pre-wrap">{c.content}</p>
                            </div>
                        ))}
                        <div className="flex gap-2">
                            <Textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Votre commentaire…"
                                className="rounded-2xl min-h-[60px]"
                                data-testid={`comment-input-${post.id}`}
                            />
                            <Button onClick={submitComment} disabled={busy || !text.trim()} className="rounded-full bg-primary text-primary-foreground self-end" data-testid={`comment-submit-${post.id}`}>
                                <Send className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
}

export default function CommunityPage() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [articles, setArticles] = useState([]);
    const [locked, setLocked] = useState(false);

    useEffect(() => {
        http.get("/community/posts")
            .then((r) => setPosts(r.data))
            .catch((e) => { if (e.response?.status === 403) setLocked(true); });
        http.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
    }, []);

    const handleLike = async (postId) => {
        try {
            const { data } = await http.post(`/community/posts/${postId}/like`);
            setPosts((ps) => ps.map((p) => p.id === postId
                ? { ...p, liked_by_me: data.liked, likes_count: data.likes_count }
                : p));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const handleCommentAdded = (postId) => {
        setPosts((ps) => ps.map((p) => p.id === postId
            ? { ...p, comments_count: (p.comments_count || 0) + 1 }
            : p));
    };

    const isAdmin = user?.role === "admin";
    if (locked || (!user?.active_subscription && !isAdmin)) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="community-locked">
                <h1 className="font-serif text-4xl mb-4">Espace réservé aux abonnés</h1>
                <p className="text-muted-foreground mb-8">
                    Rejoignez Harmonisation ou Éveil pour accéder à la communauté et à nos articles exclusifs.
                </p>
                <Link to="/abonnements">
                    <Button className="rounded-full bg-primary text-primary-foreground h-12 px-6">Voir les abonnements</Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-6 py-16" data-testid="community-page">
            <div className="mb-12">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Communauté</div>
                <h1 className="font-serif text-5xl">Le cercle</h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    Un espace privé où Olivier partage régulièrement messages canalisés, contenus exclusifs et partages. Réagissez et commentez en toute intimité.
                </p>
            </div>
            <Tabs defaultValue="feed" className="w-full">
                <TabsList className="mb-8 rounded-full">
                    <TabsTrigger value="feed" className="rounded-full" data-testid="tab-feed"><MessageCircle className="w-4 h-4 mr-2" /> Le fil</TabsTrigger>
                    <TabsTrigger value="articles" className="rounded-full" data-testid="tab-articles"><BookOpen className="w-4 h-4 mr-2" /> Articles</TabsTrigger>
                </TabsList>

                <TabsContent value="feed">
                    <div className="space-y-6">
                        {posts.length === 0 && (
                            <p className="text-muted-foreground text-center py-16">
                                Aucun partage pour l'instant. Le prochain enseignement arrive bientôt.
                            </p>
                        )}
                        {posts.map((p) => (
                            <PostCard key={p.id} post={p} onLike={handleLike} onCommentAdded={handleCommentAdded} />
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="articles">
                    <div className="grid md:grid-cols-2 gap-6">
                        {articles.map((a) => (
                            <article key={a.id} className="rounded-3xl border border-border/60 bg-card overflow-hidden" data-testid={`article-${a.id}`}>
                                {a.image_url && (
                                    <div className="aspect-[16/9] overflow-hidden">
                                        <img src={a.image_url} alt={a.image_alt || a.title} title={a.title} className="w-full h-full object-cover" loading="lazy" />
                                    </div>
                                )}
                                <div className="p-6">
                                    <h3 className="font-serif text-2xl mb-2">{a.title}</h3>
                                    <p className="text-sm text-muted-foreground mb-4">{a.excerpt}</p>
                                    <p className="text-sm leading-relaxed">{a.content}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
